/*
  Admin › Users (27) — wave 1: ideas 2 (one ⋯ menu per row instead of trash / Password / role select;
  delete asks inside the app), 3 (search with "/", family segment with counts, "Password: never changed"
  chip, "N of N people"), 5 (User = avatar + name + e-mail in Manrope, one role-pill language, Password
  changed, human dates; cards on phones) and 10 (rows grouped by role family).
  Wave 2: ideas 1 (the role pill itself opens a popover with every role and what it can open; giving or
  taking Admin rights asks first; the toast offers Undo), 4 ("account health" callout: test accounts with
  Admin rights + passwords never changed, Review / Dismiss), 6 (Reset password dialog: generate, show,
  copy, the rules ticked live) and 7 (Add user side panel: role cards that say what each role can open,
  company e-mail suggestion, the same password field).
  Wave 3: ideas 8 ("Changes · 14 days" per person — opens History filtered to them) and 9 (turn sign-in off
  instead of deleting — the person and their History stay, one click turns it back on; edit name & email;
  last sign-in under "Created").
  Final QA: "Changes · 14 days" counts History's own "Last 14 days" window (the number matches the History it
  opens); "Never signed in" only for accounts created after sign-ins started being recorded; the role popover
  fits the space above / below its row; Export CSV of the people shown; bigger touch targets on phones.
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check, ChevronDown, Download, History, KeyRound, Loader2, Power, PowerOff, ShieldAlert, Trash2, UserCog, UserPen, UserPlus, Users,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber, toDateInputValue } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { PERMISSION_MODULES, SUPER_ADMIN_ROLE } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RowActions } from '@/components/ui/row-actions';
import { FilterChip } from '@/components/ui/filter-chip';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusPill } from '@/components/ui/status-pill';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { AdminCallout, RolePill, SearchBox, Segmented, UserAvatar } from '@/components/admin/AdminUi';
import PasswordField, { copyText } from '@/components/admin/PasswordField';
import {
  accountHealth, assignableRoles, changesFor, companyEmailDomain, filterUsers, groupUsers, historyRangeStart, isAdminRole,
  isSignInOff, isTestAccount, lastSignIn, looksLikeEmail, moduleLabel, passwordIsValid, roleAccessSummary, roleChangeKind,
  signInTrackingStarted, suggestedEmail, userLoginLabel, usersCsv, userStats,
} from '@/components/admin/adminModel';

const ACTIVITY_DAYS = 14;

const FAMILY_OPTIONS = [
  { value: 'all', label: 'All', key: 'all' },
  { value: 'admins', label: 'Admins', key: 'admins' },
  { value: 'site', label: 'Site & engineering', key: 'site' },
  { value: 'office', label: 'Office', key: 'office' },
];


const article = (word) => (/^[aeiou]/i.test(word) ? 'an' : 'a');

/** "+ Invoicing", "− Admin" between two roles' access (for the role-change confirmation). */
const accessDiff = (from, to, permissions = {}) => {
  const all = PERMISSION_MODULES.map((module) => module.key);
  const before = new Set(from === SUPER_ADMIN_ROLE ? all : permissions[from] || []);
  const after = new Set(to === SUPER_ADMIN_ROLE ? all : permissions[to] || []);
  return [
    ...all.filter((key) => after.has(key) && !before.has(key)).map((key) => `+ ${moduleLabel(key)}`),
    ...all.filter((key) => before.has(key) && !after.has(key)).map((key) => `− ${moduleLabel(key)}`),
  ];
};

function TestAccountBadge() {
  return <StatusPill tone="amber" label="Test account" size="sm" className="shrink-0" />;
}

function UserCell({ user }) {
  const testAccount = isTestAccount(user);
  const off = isSignInOff(user);
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <UserAvatar name={user.name} seed={user.id || user.name} className={cn(off && 'opacity-45 grayscale')} />
      <div className="min-w-0">
        <p className={cn('flex min-w-0 items-center gap-1.5 text-[13.5px] font-semibold', off ? 'text-ink-3' : 'text-ink')}>
          <span className="truncate">{user.name}</span>
          {off ? (
            <StatusPill
              tone="gray"
              icon={PowerOff}
              label="Sign-in off"
              size="sm"
              className="shrink-0"
              title={user.sign_in_disabled_reason ? `Reason: ${user.sign_in_disabled_reason}` : undefined}
              data-testid={`user-signin-off-${user.id}`}
            />
          ) : null}
        </p>
        <p className="flex min-w-0 items-center gap-1.5 text-[12px] text-ink-3">
          <span className="truncate">{userLoginLabel(user)}</span>
          {testAccount ? <TestAccountBadge /> : null}
        </p>
      </div>
    </div>
  );
}

/** 27 #9 — rename someone or fix their sign-in e-mail; the account (and its History) stays the same. */
function EditProfileDialog({ target, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setName(target.name || '');
    setEmail(target.email || '');
    setError('');
    setSaving(false);
  }, [target]);

  if (!target) return null;
  const cleanName = name.trim().replace(/\s+/g, ' ');
  const cleanEmail = email.trim().toLowerCase();
  const changed = cleanName !== (target.name || '') || cleanEmail !== String(target.email || '').toLowerCase();
  const nameProblem = cleanName.length < 2 ? 'At least 2 characters' : '';
  const emailProblem = !cleanEmail ? 'Needed to sign in' : '';
  const emailHint = cleanEmail && !cleanEmail.includes('@') ? 'A username, not an e-mail — it still works for signing in.'
    : cleanEmail && !looksLikeEmail(cleanEmail) ? 'Check the address — it does not look like an e-mail.' : '';

  const save = async (event) => {
    event.preventDefault();
    if (!changed || nameProblem || emailProblem || saving) return;
    setSaving(true);
    setError('');
    try {
      const body = {};
      if (cleanName !== (target.name || '')) body.name = cleanName;
      if (cleanEmail !== String(target.email || '').toLowerCase()) body.email = cleanEmail;
      const res = await api.patch(`/admin/users/${target.id}`, body);
      onSaved(target, res.data || { ...target, ...body });
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the changes'));
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-[440px]" data-testid="edit-user-dialog">
        <form onSubmit={save} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Edit name &amp; email</DialogTitle>
            <DialogDescription>
              {target.name} keeps their role, password and everything they did in History.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="edit-user-name" className="text-[13px] font-semibold text-ink">Name</label>
            <Input
              id="edit-user-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              aria-invalid={Boolean(nameProblem)}
              data-testid="edit-user-name"
            />
            {nameProblem ? <p className="text-[12px] font-semibold text-status-red-ink">{nameProblem}</p> : null}
          </div>
          <div className="space-y-1.5">
            <label htmlFor="edit-user-email" className="text-[13px] font-semibold text-ink">Sign-in email</label>
            <Input
              id="edit-user-email"
              type="text"
              inputMode="email"
              autoComplete="off"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              maxLength={254}
              aria-invalid={Boolean(emailProblem)}
              aria-describedby="edit-user-email-hint"
              data-testid="edit-user-email"
            />
            <p id="edit-user-email-hint" className={cn('text-[12px]', emailProblem ? 'font-semibold text-status-red-ink' : 'text-ink-3')}>
              {emailProblem || emailHint || 'They sign in with this from now on.'}
            </p>
          </div>
          {error ? <p className="rounded-lg bg-status-red-bg px-3 py-2 text-[12.5px] text-status-red-ink" role="alert">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={!changed || Boolean(nameProblem || emailProblem) || saving} data-testid="edit-user-save">
              {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Check aria-hidden="true" />} Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The role pill — a button that opens every role with what it can open (or plain text when locked). */
function RoleMenu({ user, roles, permissions, open, onOpenChange, onPick, busy }) {
  if (!roles.length) {
    return <RolePill role={user.role} title="Only Super Admin can change an Admin's role" />;
  }
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group/role inline-flex min-h-10 max-w-full items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 md:min-h-0"
          aria-label={`Role: ${user.role}. Change role for ${user.name}`}
          disabled={busy}
          data-testid={`role-pill-${user.id}`}
        >
          <RolePill
            role={user.role}
            className={cn('pr-1.5 transition-shadow group-hover/role:shadow-[0_0_0_1px_var(--line)]', open && 'shadow-[0_0_0_1.5px_var(--brand)]')}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : <ChevronDown className="h-3 w-3 opacity-70" aria-hidden="true" />}
          </RolePill>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        avoidCollisions
        collisionPadding={12}
        // focus the current role, not the first one (arrow keys / Tab move from there)
        onOpenAutoFocus={(event) => {
          const current = event.currentTarget?.querySelector?.('[role="radio"][aria-checked="true"]');
          if (current) {
            event.preventDefault();
            current.focus();
          }
        }}
        // never taller than the room Radix measured on the chosen side — a middle row's menu used to start above the window
        className="max-h-[min(480px,var(--radix-popover-content-available-height,70vh))] w-[min(360px,calc(100vw-2rem))] overflow-y-auto rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay"
        data-testid={`role-menu-${user.id}`}
      >
        <p className="px-2.5 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Change role · {user.name}</p>
        <div role="radiogroup" aria-label={`Role for ${user.name}`}>
          {roles.map((role) => {
            const current = role === user.role;
            const summary = roleAccessSummary(role, permissions);
            return (
              <button
                key={role}
                type="button"
                role="radio"
                aria-checked={current}
                onClick={() => onPick(role)}
                className={cn(
                  'flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none',
                  current && 'bg-brand-soft hover:bg-brand-soft'
                )}
                data-testid={`role-option-${role}`}
              >
                <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center text-brand-ink">
                  {current ? <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold text-ink">{role}</span>
                  <span className="block text-[11.5px] leading-snug text-ink-3">{summary.text}</span>
                  {isAdminRole(role) ? (
                    <span className="block text-[11.5px] font-semibold leading-snug text-status-amber-ink">Can add, delete and re-role people</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** 27 #4 — the accounts that need a look before anyone else uses them. */
function AccountHealthCallout({ health, onReview, onDismiss }) {
  const accounts = health.testAdmins;
  const count = accounts.length;
  return (
    <AdminCallout
      tone="amber"
      icon={ShieldAlert}
      title={`${count} test ${count === 1 ? 'account has' : 'accounts have'} full Admin rights`}
      data-testid="users-health-callout"
      actions={(
        <>
          <Button type="button" variant="outline" size="sm" className="bg-surface max-md:h-9" onClick={onReview} data-testid="users-health-review">
            Review {count} {count === 1 ? 'account' : 'accounts'}
          </Button>
          <Button type="button" variant="ghost" size="sm" className="max-md:h-9" onClick={onDismiss} data-testid="users-health-dismiss">Dismiss</Button>
        </>
      )}
    >
      <p>
        {accounts.map((user, index) => (
          <span key={user.id}>
            {index ? (index === count - 1 ? ' and ' : ', ') : null}
            <b className="font-semibold text-ink">{user.name}</b> · {userLoginLabel(user)}
            {user.created_at ? ` (created ${formatDate(user.created_at)})` : ''}
          </span>
        ))}
        . Admins can add, delete and re-role people.
        {health.neverChanged ? ` ${health.neverChanged} of ${health.total} people have never changed their password.` : ''}
      </p>
    </AdminCallout>
  );
}

/** 27 #6 — Reset password: generate / show / copy, rules ticked live, then copied for sharing. */
function ResetPasswordDialog({ target, onClose, onDone }) {
  const confirm = useConfirm();
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedUncopied, setSavedUncopied] = useState(false);

  useEffect(() => {
    setPassword('');
    setCopied(false);
    setError('');
    setSavedUncopied(false);
  }, [target]);

  if (!target) return null;
  const valid = passwordIsValid(password);

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError('');
    try {
      await api.put(`/admin/users/${target.id}/password`, { password });
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update the password'));
      setSaving(false);
      return;
    }
    setSaving(false);
    let nowCopied = copied;
    if (!nowCopied) {
      try {
        await copyText(password);
        nowCopied = true;
      } catch {
        nowCopied = false;
      }
    }
    if (nowCopied) {
      onDone(target, { copied: true });
    } else {
      // The clipboard is blocked: keep the dialog so the password can still be copied by hand.
      setSavedUncopied(true);
      onDone(target, { copied: false, keepOpen: true });
    }
  };

  // Escape / × / a click outside while the saved password was never copied asks first.
  const requestClose = async () => {
    if (saving) return;
    if (savedUncopied && !copied) {
      const ok = await confirm({
        title: 'Close without copying the password?',
        description: `It is not shown again. ${target.name} cannot sign in with it until it is reset again.`,
        confirmLabel: 'Close anyway',
        cancelLabel: 'Copy it first',
        tone: 'danger',
      });
      if (!ok) return;
    }
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) requestClose(); }}>
      <DialogContent className="max-w-md rounded-card border-line bg-surface">
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-bold text-ink">Reset password for {target.name}</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">
              {userLoginLabel(target)} · they sign in with the new password from now on. Share it with them privately.
            </DialogDescription>
          </DialogHeader>
          {savedUncopied ? (
            <p
              className={cn(
                'rounded-lg border px-3 py-2 text-[12.5px] font-semibold',
                copied
                  ? 'border-brand-soft-border bg-brand-soft text-brand-ink'
                  : 'border-[color:color-mix(in_srgb,var(--status-amber)_38%,transparent)] bg-status-amber-bg text-status-amber-ink'
              )}
              role={copied ? 'status' : 'alert'}
              data-testid="reset-password-copy-blocked"
            >
              {copied
                ? `Password saved and copied. Share it with ${target.name} privately.`
                : 'Password saved, but copying was blocked by the browser — copy it now. It is not shown again after you close this.'}
            </p>
          ) : null}
          <PasswordField
            // once saved the field only offers Copy (shown in clear, no Generate, no editing)
            key={savedUncopied ? 'saved' : 'edit'}
            id="reset-password"
            label={savedUncopied ? 'Saved password' : 'New password'}
            value={password}
            onChange={(value) => { setPassword(value); setCopied(false); }}
            onCopied={() => setCopied(true)}
            autoFocus={!savedUncopied}
            readOnly={savedUncopied}
            testIdPrefix="reset-password"
          />
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <DialogFooter className="gap-2 sm:gap-2">
            {savedUncopied ? (
              <Button type="button" onClick={onClose} data-testid="reset-password-done">Done</Button>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button type="submit" disabled={!valid || saving} data-testid="reset-password-submit">
                  {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <KeyRound aria-hidden="true" />}
                  {saving ? 'Saving…' : 'Save password'}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_NEW_USER = { name: '', email: '', role: 'Viewer', password: '' };

/**
 * After "Create user" the browser refused the automatic copy (Safari after the await, a denied clipboard
 * permission…): the user exists, so the panel stays open on the saved password with a Copy button and a
 * Done button — it is never shown again once closed, and only Super Admin could reset it later.
 */
function CreatedPasswordView({ created, onCopied }) {
  return (
    <div className="flex flex-col gap-4" data-testid="add-user-created">
      <AdminCallout
        tone={created.copied ? 'navy' : 'amber'}
        icon={created.copied ? Check : ShieldAlert}
        live={created.copied ? 'status' : 'alert'}
        title={created.copied ? 'Password copied' : 'Copying was blocked — copy the password now'}
        data-testid="add-user-copy-blocked"
      >
        <p>
          {created.copied
            ? `Share it with ${created.name} privately. It is not shown again after you close this.`
            : `${created.name} was created, but the browser did not let us copy the password. It is not shown again after you close this.`}
        </p>
      </AdminCallout>
      <PasswordField
        id="created-user-password"
        label={`Password for ${created.name}`}
        value={created.password}
        onChange={() => {}}
        onCopied={onCopied}
        readOnly
        testIdPrefix="created-password"
      />
      <p className="text-[12px] text-ink-3">They sign in with {created.email} and this password.</p>
    </div>
  );
}

/**
 * 27 #7 — Add user: a side panel; each role card says what that role can open.
 * onCreated(user, { copied, announce, refresh }): refresh = reload the people list; announce = show the
 * "added" toast (with Undo). When the copy is blocked the list refreshes at once, but the toast waits until
 * the panel closes — so it never covers the panel's Done button and says whether the password was copied.
 */
export function AddUserPanel({ open, onOpenChange, roles, permissions = {}, users = [], canAssignSuperAdmin, onCreated }) {
  const [form, setForm] = useState(EMPTY_NEW_USER);
  const [copied, setCopied] = useState(false);
  // { user, name, email, password, copied } once the user exists but the password could not be copied for them
  const [created, setCreated] = useState(null);
  const announcedRef = useRef(false);
  const domain = useMemo(() => companyEmailDomain(users), [users]);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_NEW_USER);
      setCopied(false);
      setCreated(null);
      announcedRef.current = false;
    }
  }, [open]);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  // Before saving: unsaved form. After saving with a blocked copy: a password nobody has copied yet.
  const dirty = created ? !created.copied : Boolean(form.name.trim() || form.email.trim() || form.password);
  const emailSuggestion = suggestedEmail(form.email, domain);
  const options = roles.filter((role) => role !== SUPER_ADMIN_ROLE || canAssignSuperAdmin);

  const save = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    if (name.length < 2) throw new Error('Enter their name (2 characters or more)');
    if (!looksLikeEmail(email)) throw new Error(`Enter their company e-mail${domain ? `, e.g. name@${domain}` : ''}`);
    if (!passwordIsValid(form.password)) throw new Error('The password does not meet the rules yet');
    const res = await api.post('/admin/users', { name, email, role: form.role, password: form.password });
    let wasCopied = copied;
    if (!wasCopied) {
      try {
        await copyText(form.password);
        wasCopied = true;
      } catch {
        wasCopied = false;
      }
    }
    if (wasCopied) {
      onCreated?.(res.data, { copied: true, announce: true, refresh: true });
      onOpenChange?.(false);
      return;
    }
    onCreated?.(res.data, { copied: false, announce: false, refresh: true });
    setCreated({ user: res.data, name: res.data?.name || name, email: res.data?.email || email, password: form.password, copied: false });
  };

  // Closing after a blocked copy (Done, × or Escape): now announce it, saying whether it was copied by hand.
  const handleOpenChange = (next) => {
    if (!next && created && !announcedRef.current) {
      announcedRef.current = true;
      onCreated?.(created.user, { copied: created.copied, announce: true, refresh: false });
    }
    onOpenChange?.(next);
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={handleOpenChange}
      width="md"
      icon={UserPlus}
      title={created ? `${created.name} added` : 'Add user'}
      subtitle={created ? 'Copy the password before you close this panel.' : 'They sign in with their company e-mail and this password.'}
      dirty={dirty}
      onSave={created ? undefined : save}
      closeOnSave={false}
      saveLabel="Create user"
      confirmOptions={created ? {
        title: 'Close without copying the password?',
        description: `It is not shown again. If nobody has it, only a Super Admin can reset ${created.name}’s password.`,
        confirmLabel: 'Close anyway',
        cancelLabel: 'Copy it first',
      } : undefined}
      footer={created ? (
        <footer className="flex items-center justify-end gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-[22px] sm:py-3.5">
          <Button type="button" onClick={() => handleOpenChange(false)} data-testid="add-user-done">Done</Button>
        </footer>
      ) : undefined}
      testId="add-user-panel"
    >
      {created ? (
        <CreatedPasswordView created={created} onCopied={() => setCreated((current) => (current ? { ...current, copied: true } : current))} />
      ) : (
      <div className="flex flex-col gap-5" data-testid="user-form">
        <SidePanelSection title="Person">
          <div className="space-y-1">
            <label htmlFor="new-user-name" className="text-[12.5px] font-semibold text-ink-2">Name</label>
            <Input
              id="new-user-name"
              value={form.name}
              onChange={(event) => set({ name: event.target.value })}
              autoComplete="off"
              className="h-9 rounded-control border-line bg-surface text-[13.5px] text-ink"
              data-testid="user-name-input"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="new-user-email" className="text-[12.5px] font-semibold text-ink-2">Company e-mail</label>
            <Input
              id="new-user-email"
              type="email"
              value={form.email}
              onChange={(event) => set({ email: event.target.value })}
              placeholder={domain ? `name@${domain}` : 'name@company.com'}
              autoComplete="off"
              className="h-9 rounded-control border-line bg-surface text-[13.5px] text-ink"
              data-testid="user-email-input"
            />
            {emailSuggestion ? (
              <button
                type="button"
                onClick={() => set({ email: emailSuggestion })}
                className="inline-flex items-center gap-1 rounded text-[12px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="user-email-suggestion"
              >
                Use {emailSuggestion}
              </button>
            ) : null}
          </div>
        </SidePanelSection>

        <SidePanelSection title="Role" description="What they can open — you can change it later from the role pill.">
          <div role="radiogroup" aria-label="Role" className="grid gap-2 sm:grid-cols-2" data-testid="user-role-cards">
            {options.map((role) => {
              const checked = form.role === role;
              const summary = roleAccessSummary(role, permissions);
              return (
                <button
                  key={role}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  onClick={() => set({ role })}
                  className={cn(
                    'rounded-xl border bg-surface px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    checked ? 'border-brand bg-brand-soft shadow-[inset_0_0_0_1px_var(--brand)]' : 'border-line hover:bg-surface-hover'
                  )}
                  data-testid={`user-role-option-${role}`}
                >
                  <span className="flex items-center gap-1.5 text-[13px] font-bold text-ink">
                    {role}
                    {checked ? <Check className="ml-auto h-3.5 w-3.5 text-brand-ink" strokeWidth={3} aria-hidden="true" /> : null}
                  </span>
                  <span className="mt-0.5 block text-[11.5px] leading-snug text-ink-3">{summary.text}</span>
                  {isAdminRole(role) ? (
                    <span className="mt-0.5 block text-[11.5px] font-semibold leading-snug text-status-amber-ink">Can add, delete and re-role people</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </SidePanelSection>

        <SidePanelSection title="Password">
          <PasswordField
            id="new-user-password"
            label="Password"
            value={form.password}
            onChange={(password) => { set({ password }); setCopied(false); }}
            onCopied={() => setCopied(true)}
            testIdPrefix="user-password"
          />
        </SidePanelSection>
      </div>
      )}
    </SidePanel>
  );
}

/**
 * healthDismissed / onHealthDismiss: the page owns this browser's "Dismiss" of the account-health callout,
 * so the amber dot on the Users tab hides together with the callout (same set of test accounts).
 */
export default function UsersTab({
  users, loading, roles, currentUser, refreshUsers, setUsers, onViewActivity, onPeopleChanged, rolePermissions = {},
  healthDismissed = '', onHealthDismiss, signInOffEnforced = false, signInTrackingSince = '',
}) {
  const confirm = useConfirm();
  const searchRef = useRef(null);
  const [search, setSearch] = useState('');
  const [family, setFamily] = useState('all');
  const [passwordNever, setPasswordNever] = useState(false);
  const [reviewIds, setReviewIds] = useState(null);
  const [roleMenuFor, setRoleMenuFor] = useState('');
  const [busyId, setBusyId] = useState('');
  const [passwordFor, setPasswordFor] = useState(null);
  const [profileFor, setProfileFor] = useState(null);
  const [highlightId, setHighlightId] = useState('');
  const [activity, setActivity] = useState(null);
  const reasonRef = useRef(null);

  const viewerIsSuperAdmin = currentUser?.role === SUPER_ADMIN_ROLE;
  const stats = useMemo(() => userStats(users), [users]);
  const health = useMemo(() => accountHealth(users), [users]);
  const visible = useMemo(() => {
    const filtered = filterUsers(users, { search, family, passwordNever });
    return reviewIds ? filtered.filter((user) => reviewIds.includes(user.id)) : filtered;
  }, [users, search, family, passwordNever, reviewIds]);
  const groups = useMemo(() => groupUsers(visible), [visible]);
  const showHealth = health.needsLook && healthDismissed !== health.signature;
  const tracking = useMemo(() => signInTrackingStarted(users), [users]);
  const signInOffCount = useMemo(() => users.filter(isSignInOff).length, [users]);

  // 27 #8 — changes per person in the last 14 days (counted on the server; refreshed with the list). The window is
  // History's own "Last 14 days" (since = its date_from), so the number matches the History page it opens.
  const loadActivity = useCallback(async () => {
    try {
      const res = await api.get('/admin/users/activity', { params: { days: ACTIVITY_DAYS, since: historyRangeStart('14d') } });
      setActivity(res.data?.counts || {});
    } catch {
      setActivity(null);
    }
  }, []);
  useEffect(() => { loadActivity(); }, [loadActivity, users.length]);

  // "/" focuses the search (unless you are already typing somewhere)
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = (event.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!highlightId) return undefined;
    const timer = setTimeout(() => setHighlightId(''), 6000);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const onRoleChanged = useCallback((target, updated) => {
    setUsers((current) => current.map((user) => (user.id === target.id ? { ...user, role: updated.role } : user)));
    setHighlightId(target.id);
    onPeopleChanged?.();
    const undoId = updated.undo_id;
    notify.success(`${target.name} is now ${updated.role}`, undoId ? {
      undo: async () => {
        await api.post(`/undo/entries/${undoId}`);
        await refreshUsers();
        onPeopleChanged?.();
      },
      undoneMessage: `${target.name} is ${target.role} again`,
    } : undefined);
  }, [refreshUsers, setUsers, onPeopleChanged]);

  /** Admin rights given or taken away ask first; any other change saves at once (the toast can undo). */
  const changeRole = async (target, role) => {
    setRoleMenuFor('');
    if (!role || role === target.role) return;
    const kind = roleChangeKind(target.role, role);
    const put = async () => (await api.put(`/admin/users/${target.id}`, { role })).data;
    if (kind !== 'plain') {
      let updated = null;
      const diff = accessDiff(target.role, role, rolePermissions);
      const ok = await confirm({
        title: kind === 'promote'
          ? `Make ${target.name} ${article(role)} ${role}?`
          : kind === 'demote'
            ? `Take ${target.name}'s Admin rights away?`
            : `Change ${target.name} to ${role}?`,
        subtitle: `${target.role} → ${role} · ${userLoginLabel(target)}`,
        description: kind === 'promote'
          ? `${role === SUPER_ADMIN_ROLE ? 'Super Admin can do everything, including deleting projects and changing every role’s access' : 'Admins can add, delete and re-role people and change company settings'}. What changes for ${target.name}:`
          : kind === 'demote'
            ? `${target.name} can no longer manage people, settings or access. What changes:`
            : 'What changes:',
        consequences: diff.length ? diff : ['Same pages and project tabs as now'],
        confirmLabel: kind === 'promote' ? `Make ${role}` : 'Change role',
        tone: kind === 'demote' ? 'danger' : 'default',
        icon: ShieldAlert,
        onConfirm: async () => { updated = await put(); },
      });
      if (!ok) return;
      onRoleChanged(target, updated || { ...target, role });
      return;
    }
    setBusyId(target.id);
    try {
      const updated = await put();
      onRoleChanged(target, updated || { ...target, role });
    } catch (err) {
      notify.error('Could not change the role', { description: getErrorMessage(err) });
    } finally {
      setBusyId('');
    }
  };

  const onPasswordDone = (target, { copied, keepOpen }) => {
    if (!keepOpen) setPasswordFor(null);
    if (copied) {
      notify.success('Password updated · copied to clipboard', { description: `Share it with ${target.name} privately.` });
    } else {
      notify.warning('Password updated · not copied', { description: 'The browser blocked copying — copy it from the dialog before you close it.' });
    }
    refreshUsers();
  };

  const deleteUser = async (target) => {
    let undoId = '';
    const ok = await confirm({
      title: `Delete ${target.name}?`,
      subtitle: `${target.role} · ${userLoginLabel(target)}`,
      description: 'They can no longer sign in. Their name stays on the changes they made in History.',
      tone: 'danger',
      confirmLabel: 'Delete user',
      note: isAdminRole(target.role) ? 'Only Super Admin can delete Admins.' : undefined,
      onConfirm: async () => {
        const res = await api.delete(`/admin/users/${target.id}`);
        undoId = res.data?.undo_id || '';
      },
    });
    if (!ok) return;
    setUsers((current) => current.filter((user) => user.id !== target.id));
    onPeopleChanged?.();
    notify.success(`${target.name} deleted`, undoId ? {
      undo: async () => {
        await api.post(`/undo/entries/${undoId}`);
        await refreshUsers();
        onPeopleChanged?.();
      },
    } : undefined);
  };

  const replaceUser = (updated) => setUsers((current) => current.map((user) => (user.id === updated.id ? { ...user, ...updated } : user)));

  const onProfileSaved = (target, updated) => {
    setProfileFor(null);
    replaceUser(updated);
    setHighlightId(target.id);
    const undoId = updated.undo_id;
    const what = [
      updated.name !== target.name ? `${target.name} → ${updated.name}` : '',
      updated.email !== target.email ? `Email: ${updated.email}` : '',
    ].filter(Boolean).join(' · ');
    notify.success('Name & email saved', {
      description: what,
      ...(undoId ? {
        undo: async () => { await api.post(`/undo/entries/${undoId}`); await refreshUsers(); },
        undoneMessage: `${target.name}'s details are back`,
      } : {}),
    });
    loadActivity();
  };

  /** 27 #9 — sign-in off (asks first, optional reason) / back on (at once); both undoable from the toast. */
  const setSignIn = async (target, enabled) => {
    let updated = null;
    const put = async () => {
      const reason = enabled ? '' : String(reasonRef.current?.value || '').trim();
      updated = (await api.put(`/admin/users/${target.id}/sign-in`, { enabled, reason })).data;
    };
    if (!enabled) {
      const ok = await confirm({
        title: `Turn off sign-in for ${target.name}?`,
        subtitle: `${target.role} · ${userLoginLabel(target)}`,
        description: `${target.name} can no longer sign in, and devices that stayed signed in are signed out. Nothing is deleted:`,
        children: (
          <div className="space-y-1.5">
            <ul className="mb-3 list-disc space-y-1 pl-5 text-[13px] text-ink-2">
              <li>Their name stays on everything they did in History</li>
              <li>Their role and access stay as they are</li>
              <li>Turn it back on any time from the ⋯ menu</li>
            </ul>
            <label htmlFor="signin-off-reason" className="text-[12.5px] font-semibold text-ink-2">Reason (optional)</label>
            <Input id="signin-off-reason" ref={reasonRef} maxLength={200} placeholder="e.g. Left the company" data-testid="signin-off-reason" />
          </div>
        ),
        confirmLabel: 'Turn off sign-in',
        // a red action, but nothing is deleted: no trash can on the button (confirmIcon: null)
        tone: 'danger',
        icon: PowerOff,
        confirmIcon: null,
        onConfirm: put,
      });
      if (!ok) return;
    } else {
      setBusyId(target.id);
      try {
        await put();
      } catch (err) {
        notify.error('Could not turn sign-in back on', { description: getErrorMessage(err) });
        return;
      } finally {
        setBusyId('');
      }
    }
    const next = updated || { ...target, sign_in_disabled: !enabled };
    replaceUser(next);
    setHighlightId(target.id);
    const undoId = next.undo_id;
    notify.success(enabled ? `${target.name} can sign in again` : `${target.name} can no longer sign in`, {
      description: enabled ? 'With the same role and access as before.' : 'Their History stays. Turn it back on from the ⋯ menu.',
      ...(undoId ? {
        undo: async () => { await api.post(`/undo/entries/${undoId}`); await refreshUsers(); },
        undoneMessage: enabled ? `${target.name}'s sign-in is off again` : `${target.name} can sign in again`,
      } : {}),
    });
  };

  const rowActions = (user) => {
    const isSelf = user.id === currentUser?.id;
    const needsSuperAdmin = isAdminRole(user.role) && !viewerIsSuperAdmin;
    const off = isSignInOff(user);
    return [
      {
        label: 'Edit name & email…', icon: UserPen, onSelect: () => setProfileFor(user), testId: `edit-user-${user.id}`,
        disabled: needsSuperAdmin && !isSelf, hint: needsSuperAdmin && !isSelf ? 'Super Admin' : undefined,
      },
      {
        label: 'Change role…', icon: UserCog, onSelect: () => setRoleMenuFor(user.id), testId: `change-role-${user.id}`,
        disabled: needsSuperAdmin, hint: needsSuperAdmin ? 'Super Admin' : undefined,
      },
      viewerIsSuperAdmin && { label: 'Reset password…', icon: KeyRound, onSelect: () => setPasswordFor(user), testId: `password-user-${user.id}` },
      { label: 'View activity', icon: History, onSelect: () => onViewActivity?.(user), testId: `user-activity-${user.id}` },
      off ? {
        label: 'Turn sign-in back on', icon: Power, onSelect: () => setSignIn(user, true), testId: `signin-on-${user.id}`,
        disabled: needsSuperAdmin || busyId === user.id, hint: needsSuperAdmin ? 'Super Admin' : undefined,
      } : signInOffEnforced ? {
        label: 'Disable sign-in…', icon: PowerOff, onSelect: () => setSignIn(user, false), testId: `signin-off-${user.id}`,
        disabled: isSelf || needsSuperAdmin, hint: isSelf ? 'You' : needsSuperAdmin ? 'Super Admin' : 'New',
      } : {
        // Password sign-in does not check the switch yet (the server refuses it until it does): never offer a
        // lock-out that does not lock anyone out. Reset password / Delete user do lock someone out today.
        label: 'Disable sign-in', icon: PowerOff, onSelect: () => {}, testId: `signin-off-${user.id}`, disabled: true, hint: 'Soon',
      },
      {
        label: 'Delete user…', icon: Trash2, danger: true, onSelect: () => deleteUser(user), testId: `delete-user-${user.id}`,
        disabled: isSelf || needsSuperAdmin, hint: isSelf ? 'You' : needsSuperAdmin ? 'Super Admin' : undefined,
      },
    ];
  };

  const review = () => {
    setSearch('');
    setFamily('all');
    setPasswordNever(false);
    setReviewIds(health.testAdmins.map((user) => user.id));
  };

  const dismissHealth = () => {
    onHealthDismiss?.(health.signature);
    setReviewIds(null);
  };

  const counts = { all: stats.all, admins: stats.admins, site: stats.site, office: stats.office };
  const signInOptions = { tracking, trackingSince: signInTrackingSince };

  const exportCsv = () => {
    if (!visible.length) {
      notify.info('Nothing to export', { description: 'Nobody matches these filters.' });
      return;
    }
    try {
      const blob = new Blob([`\uFEFF${usersCsv(visible, { activity, trackingSince: signInTrackingSince })}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `people-${toDateInputValue(new Date())}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      notify.success(`Exported ${visible.length} ${visible.length === 1 ? 'person' : 'people'}`);
    } catch (err) {
      notify.error('Could not export the people', { description: getErrorMessage(err) });
    }
  };
  const hasFilters = Boolean(search.trim()) || family !== 'all' || passwordNever || Boolean(reviewIds);
  const clearFilters = () => { setSearch(''); setFamily('all'); setPasswordNever(false); setReviewIds(null); };

  return (
    <div className="space-y-4">
      {showHealth ? <AccountHealthCallout health={health} onReview={review} onDismiss={dismissHealth} /> : null}

      {/* Toolbar: search · family · password chip ……… N of N people */}
      <div className="flex flex-wrap items-center gap-2" data-testid="users-toolbar">
        <SearchBox
          ref={searchRef}
          value={search}
          onChange={setSearch}
          placeholder="Search name or email"
          className="w-full sm:w-[280px]"
          testId="users-search"
        />
        <div className="max-w-full overflow-x-auto">
          <Segmented
            label="Role family"
            value={family}
            onChange={setFamily}
            testIdPrefix="users-family"
            options={FAMILY_OPTIONS.map((option) => ({ ...option, count: counts[option.key] }))}
            buttonClassName="h-9 md:h-[30px]"
          />
        </div>
        <FilterChip
          label={passwordNever ? 'Password' : 'Password: never changed'}
          value={passwordNever ? 'never changed' : null}
          onClick={() => setPasswordNever((current) => !current)}
          onClear={() => setPasswordNever(false)}
          className="h-9 md:h-[30px]"
          data-testid="users-password-chip"
        />
        {reviewIds ? (
          <FilterChip
            label="Showing"
            value={`${reviewIds.length} test ${reviewIds.length === 1 ? 'account' : 'accounts'} with Admin`}
            onClear={() => setReviewIds(null)}
            data-testid="users-review-chip"
          />
        ) : null}
        <span className="ml-auto whitespace-nowrap text-[12.5px] text-ink-3" data-testid="users-count">
          {visible.length} of {stats.all} {stats.all === 1 ? 'person' : 'people'}
        </span>
      </div>

      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
        {loading && !users.length ? (
          <div className="space-y-2 p-4" aria-busy="true" aria-label="Loading people">
            {[0, 1, 2, 3, 4].map((index) => <div key={index} className="h-11 animate-pulse rounded-lg bg-surface-2" />)}
          </div>
        ) : !visible.length ? (
          <EmptyState
            icon={Users}
            title={hasFilters ? 'Nobody matches these filters' : 'No people yet'}
            text={hasFilters ? 'Try another name or clear the filters.' : 'Add the first person with “Add user”.'}
            action={hasFilters ? <Button variant="outline" onClick={clearFilters}>Clear filters</Button> : null}
          />
        ) : (
          <>
            {/* One table: grouped rows on desktop; on phones each row folds into a card (name · role · ⋯) */}
            <table className="block w-full border-collapse md:table md:table-fixed" data-testid="users-table">
              <colgroup>
                <col />
                <col className="w-[200px]" />
                <col className="w-[132px]" />
                <col className="w-[140px]" />
                <col className="w-[160px]" />
                <col className="w-[52px]" />
              </colgroup>
              <thead className="hidden md:table-header-group">
                <tr className="border-b border-line">
                  <th scope="col" className="h-9 bg-surface-2 px-4 text-left text-[11.5px] font-semibold text-ink-3">User</th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3">Role</th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-right text-[11.5px] font-semibold text-ink-3" data-testid="users-changes-head">
                    <span title={`Changes saved in History in the last ${ACTIVITY_DAYS} days`}>Changes · {ACTIVITY_DAYS} days</span>
                  </th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3">Password changed</th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3">Created · last sign-in</th>
                  <th scope="col" className="h-9 bg-surface-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              {groups.map((group) => (
                <tbody key={group.key} className="block md:table-row-group" data-testid={`users-group-${group.key}`}>
                  <tr className="block border-b border-line-2 bg-surface-2 md:table-row">
                    <th scope="rowgroup" colSpan={6} className="block px-4 py-2 text-left text-[12.5px] font-normal text-ink-3 md:table-cell md:h-9 md:py-0">
                      <span className="font-bold text-ink">{group.label}</span>
                      <span className="ml-1.5 rounded-full bg-[color:var(--status-gray-bg)] px-1.5 py-px text-[11px] font-bold tabular-nums text-ink-2">{group.users.length}</span>
                      <span className="ml-3 hidden sm:inline">{group.note}</span>
                    </th>
                  </tr>
                  {group.users.map((user) => (
                    <tr
                      key={user.id}
                      className={cn(
                        'group flex items-center gap-3 border-b border-line-2 px-4 py-2.5 transition-colors last:border-b-0 hover:bg-surface-hover md:table-row md:p-0',
                        highlightId === user.id && 'bg-[color:var(--status-lime-bg)] hover:bg-[color:var(--status-lime-bg)]',
                        isSignInOff(user) && highlightId !== user.id && 'bg-surface-2/60',
                        roleMenuFor === user.id && 'bg-surface-hover'
                      )}
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="min-w-0 flex-1 md:h-[52px] md:px-4 md:align-middle"><UserCell user={user} /></td>
                      <td className="max-w-[148px] shrink-0 md:max-w-none md:px-3 md:align-middle">
                        <RoleMenu
                          user={user}
                          roles={assignableRoles(roles, { viewerIsSuperAdmin, target: user })}
                          permissions={rolePermissions}
                          open={roleMenuFor === user.id}
                          onOpenChange={(open) => setRoleMenuFor(open ? user.id : '')}
                          onPick={(role) => changeRole(user, role)}
                          busy={busyId === user.id}
                        />
                      </td>
                      <td className="hidden px-3 text-right align-middle text-[13px] tabular-nums md:table-cell" data-testid={`user-changes-${user.id}`}>
                        {activity === null ? (
                          <span className="text-ink-4" aria-label="Not loaded">—</span>
                        ) : changesFor(activity, user) > 0 ? (
                          <button
                            type="button"
                            onClick={() => onViewActivity?.(user, { range: '14d' })}
                            className="rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={`${changesFor(activity, user)} changes by ${user.name} in the last ${ACTIVITY_DAYS} days — open in History`}
                          >
                            {formatNumber(changesFor(activity, user))}
                          </button>
                        ) : (
                          <span className="text-ink-4" aria-label="No changes">—</span>
                        )}
                      </td>
                      <td className={cn('hidden px-3 align-middle text-[13px] md:table-cell', user.password_changed_at ? 'text-ink' : 'text-ink-3')}>
                        {user.password_changed_at ? formatDate(user.password_changed_at) : 'Never'}
                      </td>
                      <td className="hidden px-3 align-middle text-[13px] text-ink md:table-cell">
                        {formatDate(user.created_at)}
                        {(() => {
                          // red only for an account created after sign-ins started being recorded and never used since
                          const signIn = lastSignIn(user, signInOptions);
                          return signIn.text ? (
                            <span
                              className={cn(
                                'block text-[12px]',
                                signIn.tone === 'off' ? 'font-semibold text-ink-2' : signIn.tone === 'red' ? 'text-status-red-ink' : 'text-ink-3'
                              )}
                              data-tone={signIn.tone}
                              data-testid={`user-last-signin-${user.id}`}
                            >
                              {signIn.text}
                            </span>
                          ) : null;
                        })()}
                      </td>
                      <td className="shrink-0 text-right md:pr-3 md:align-middle">
                        <RowActions
                          label={`Actions for ${user.name}`}
                          items={rowActions(user)}
                          className="h-10 w-10 md:h-7 md:w-7"
                          data-testid={`user-actions-${user.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3 text-[12.5px] text-ink-3">
              <span data-testid="users-footer">
                {stats.all} {stats.all === 1 ? 'person' : 'people'} · {stats.admins} with Admin access
                {stats.neverChangedPassword ? ` · ${stats.neverChangedPassword} never changed their password` : ''}
                {signInOffCount ? ` · ${signInOffCount} with sign-in off` : ''}
              </span>
              <button
                type="button"
                onClick={exportCsv}
                className="inline-flex min-h-9 items-center gap-1.5 rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-0"
                title={hasFilters ? `The ${visible.length} people shown` : 'Everyone'}
                data-testid="users-export"
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" /> Export CSV
              </button>
            </div>
          </>
        )}
      </div>

      <ResetPasswordDialog target={passwordFor} onClose={() => setPasswordFor(null)} onDone={onPasswordDone} />
      <EditProfileDialog target={profileFor} onClose={() => setProfileFor(null)} onSaved={onProfileSaved} />
    </div>
  );
}
